import type { PostProcessorModule, TOptions } from 'i18next';

/** A French word starting with a vowel sound (mute h aside). */
const VOWEL_START = /^[aeiouyàâäæéèêëîïôöœùûü]/iu;

/** i18next options that are settings, not interpolated values. */
const NOT_VALUES = new Set([
  'lng',
  'lngs',
  'ns',
  'context',
  'defaultValue',
  'postProcess',
  'interpolation',
  'replace',
]);

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * French elision before an interpolated value: "la page de {{name}}" with
 * Emma reads "la page d'Emma", and "que {{name}}" reads "qu'Émile". Only the
 * words right before an interpolated value that starts with a vowel change,
 * so the static text of a translation is never touched.
 */
export const elideFrench = (
  text: string,
  values: Record<string, unknown>
): string =>
  Object.entries(values).reduce((out, [key, value]) => {
    if (NOT_VALUES.has(key) || typeof value !== 'string') return out;
    if (!VOWEL_START.test(value)) return out;
    const before = new RegExp(
      `(^|[^\\p{L}])(d|D|qu|Qu|QU)e (?=${escapeRegExp(value)})`,
      'gu'
    );
    return out.replace(before, "$1$2'");
  }, text);

/**
 * i18next post-processor applying `elideFrench` to French translations.
 * Register with `.use(frenchElision)` and `postProcess: ['frenchElision']`.
 */
export const frenchElision: PostProcessorModule = {
  type: 'postProcessor',
  name: 'frenchElision',
  process: (value: string, _key, options: TOptions, translator) => {
    const lng =
      (options?.lng as string | undefined) ??
      (translator as { language?: string } | undefined)?.language;
    if (!lng?.startsWith('fr')) return value;
    const replace = (options?.replace ?? {}) as Record<string, unknown>;
    return elideFrench(value, { ...options, ...replace });
  },
};
